import { Navigate, Route, Routes } from 'react-router-dom';

import { AppsPage } from './AppsPage';
import { DeviceCategoriesPage } from './DeviceCategoriesPage';
import { EmployeesPage } from './EmployeesPage';
import { GameAccountsPage } from './GameAccountsPage';
import { GamesPage } from './GamesPage';
import { LoyaltyPage } from './LoyaltyPage';
import { PaymentProvidersPage } from './PaymentProvidersPage';
import { PortalPage } from './PortalPage';
import { PrintQueuePage } from './PrintQueuePage';
import { PrintingPage } from './PrintingPage';
import { RolesPage } from './RolesPage';
import { SettingsLayout } from './SettingsLayout';
import { VenuePage } from './VenuePage';

/**
 * Settings routes (SPEC §6).
 *
 * Mounted under `/settings/*` from App.tsx, so `path` here is relative — but
 * every `Navigate to` is ABSOLUTE on purpose. Inside a splat route a relative
 * `to` resolves against the whole matched pathname, splat included, so the `*`
 * fallback would have redirected `/settings/bogus` to `/settings/bogus/general`,
 * matched `*` again and looped forever. Portal Content shipped that bug.
 *
 * The old §6.1 General Settings page was FOUR pages' worth of form behind one
 * Confirm; it is now four routes — `general` (the venue itself), `printing`,
 * `portal` and `loyalty`. `general` keeps its path rather than becoming
 * `venue`, because it is the area's landing screen and every bookmark, every
 * redirect below and the owner's own habit point at it.
 *
 * `categories` and `products` REDIRECT to the cafe module (wave 3, #16): the
 * real screens have lived under /cafe all along — the cafe module's own
 * header comment invites exactly these redirects. They no longer appear in the
 * settings nav (they were never settings), but the routes stay: a bookmark or
 * a habit must not 404.
 *
 * Same rule for the two screens renamed on 2026-09-20 — `electronics` is now
 * `payments` and `print-jobs` is now `print-queue`. The old paths redirect and
 * are never removed.
 *
 * And for the two pages the owner removed on 2026-09-24: `public` redirects to
 * the Venue page, which now holds the two things on it that did something (the
 * venue name and the staff-app colours); `device-groups` redirects to Device
 * categories, the page it sat beside.
 */
export function SettingsRoutes() {
  return (
    <Routes>
      <Route element={<SettingsLayout />}>
        {/* General Settings is the landing screen for the area. */}
        <Route index element={<Navigate to="/settings/general" replace />} />
        <Route path="general" element={<VenuePage />} />
        <Route path="printing" element={<PrintingPage />} />
        <Route path="portal" element={<PortalPage />} />
        <Route path="loyalty" element={<LoyaltyPage />} />
        <Route path="public" element={<Navigate to="/settings/general" replace />} />
        <Route path="categories" element={<Navigate to="/cafe/taxonomy" replace />} />
        <Route path="products" element={<Navigate to="/cafe/products" replace />} />
        <Route path="employees" element={<EmployeesPage />} />
        {/* Wave 3 — role templates (agent G) and the print-job queue (agent D). */}
        <Route path="roles" element={<RolesPage />} />
        <Route path="print-queue" element={<PrintQueuePage />} />
        <Route path="print-jobs" element={<Navigate to="/settings/print-queue" replace />} />
        <Route path="game-accounts" element={<GameAccountsPage />} />
        <Route path="games" element={<GamesPage />} />
        <Route path="apps" element={<AppsPage />} />
        <Route path="payments" element={<PaymentProvidersPage />} />
        <Route path="electronics" element={<Navigate to="/settings/payments" replace />} />
        <Route path="device-categories" element={<DeviceCategoriesPage />} />
        <Route
          path="device-groups"
          element={<Navigate to="/settings/device-categories" replace />}
        />
        <Route path="*" element={<Navigate to="/settings/general" replace />} />
      </Route>
    </Routes>
  );
}
